import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, ChevronDown } from 'lucide-react';
import Section from './Section';

const faqs = [
  {
    question: "How does BrandContentFlow work?",
    answer: "Simply upload your blog post, article, or any long-form content. Our AI analyzes your brand voice and automatically transforms it into platform-optimized content for LinkedIn, Instagram, email newsletters, and more — all in under 10 minutes."
  },
  {
    question: "What platforms do you support?",
    answer: "We currently support LinkedIn posts, Instagram carousels and captions, email newsletters, blog summaries, and ad copy. We're constantly adding new platforms based on user feedback."
  },
  {
    question: "Will the content sound like my brand?",
    answer: "Yes! BrandContentFlow learns your unique brand voice, tone, and style from your existing content. Every piece of generated content maintains 100% consistency with your brand identity across all platforms."
  },
  {
    question: "How much time will I actually save?",
    answer: "Most teams report saving 5-8 hours per week on content repurposing. What used to take 6 hours of manual rewriting now takes just 10 minutes. That's time you can reinvest into strategy and creativity."
  },
  {
    question: "When will BrandContentFlow launch?",
    answer: "We're currently in private beta with select teams. Join the waitlist to get early access and be among the first to transform your content workflow. Early adopters will receive special founding member pricing."
  }
];

const FAQItem = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <motion.div 
      className="border-b border-slate-200 last:border-b-0"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
    >
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between text-left group"
        data-testid={`faq-question-${index}`}
      >
        <span className="text-lg font-semibold text-slate-800 pr-4 group-hover:text-indigo-600 transition-colors">
          {question}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="shrink-0"
        >
          <ChevronDown className={`w-5 h-5 ${isOpen ? 'text-indigo-600' : 'text-slate-400'}`} />
        </motion.div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-slate-600 leading-relaxed" data-testid={`faq-answer-${index}`}>
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(-1);

  return (
    <Section id="faq" background="alt" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 rounded-full text-sm font-medium text-indigo-600 mb-4">
            <HelpCircle className="w-4 h-4" />
            FAQ
          </span>
          <h2 
            className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
            style={{ textWrap: 'balance' }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-slate-500 text-lg">
            Everything you need to know about BrandContentFlow.
          </p>
        </motion.div>

        {/* FAQ List */}
        <motion.div 
          className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              index={index}
            />
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

export default FAQ;
